<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { createMolliePaymentFn } from '@/services/api/restApiCalls'

const MOLLIE_SCRIPT_ID = 'mollie-js-v1'

function loadMollieScript() {
  return new Promise((resolve, reject) => {
    if (window.Mollie) {
      resolve(window.Mollie)
      return
    }
    const existing = document.getElementById(MOLLIE_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Mollie))
      existing.addEventListener('error', reject)
      return
    }
    const script = document.createElement('script')
    script.id = MOLLIE_SCRIPT_ID
    script.src = 'https://js.mollie.com/v1/mollie.js'
    script.async = true
    script.onload = () => resolve(window.Mollie)
    script.onerror = reject
    document.head.appendChild(script)
  })
}

export default {
  props: {
    profileId: { type: String, required: true },
    testmode: { type: Boolean, default: false },
    beforePlaceOrder: { type: Function, default: null }
  },

  emits: ['pay-button-state'],

  setup(props, { expose, emit }) {
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const cartStore = useCartGLobalStore()

    const mollie = ref(null)
    const cardComponent = ref(null)
    const cardErrorMsg = ref('')
    const payButtonDisabled = ref(true)
    const isProcessing = ref(false)
    const isReady = ref(false)
    const isUnmounted = ref(false)

    const formattedAmount = computed(() => {
      const total = cartStore.CART_DATA?.order_total || 0
      return `£${Number(total).toFixed(2)}`
    })

    const updatePayButtonState = (event = {}) => {
      if (event.error) {
        cardErrorMsg.value = event.error
        payButtonDisabled.value = true
        return
      }
      cardErrorMsg.value = ''
      if (typeof event.valid === 'boolean') {
        payButtonDisabled.value = !event.valid
        return
      }
      if (typeof event.complete === 'boolean') {
        payButtonDisabled.value = !event.complete
        return
      }
      payButtonDisabled.value = !isReady.value
    }

    const mountCardForm = () => {
      if (!mollie.value) return
      cardComponent.value = mollie.value.createComponent('card')
      cardComponent.value.mount('#mollie-card-form')
      cardComponent.value.addEventListener('change', (event) => {
        updatePayButtonState(event)
      })
      isReady.value = true
      payButtonDisabled.value = false
    }

    const handleMolliePayment = async () => {
      if (isUnmounted.value || isProcessing.value || !isReady.value) return

      isProcessing.value = true
      cardErrorMsg.value = ''

      try {
        if (props.beforePlaceOrder) {
          const ready = await props.beforePlaceOrder()
          if (!ready) {
            isProcessing.value = false
            return
          }
        }

        const { token, error } = await mollie.value.createToken()
        if (error) {
          cardErrorMsg.value = error.message || 'Card validation failed'
          payButtonDisabled.value = true
          isProcessing.value = false
          return
        }

        const response = await createMolliePaymentFn(restoStore?.RESTO_ID, token)
        if (!response || response?.status >= 400 || response?.error_code) {
          cardErrorMsg.value = response?.status_msg || 'Payment failed. Please try again.'
          isProcessing.value = false
          return
        }

        if (response.order_status === 'AUTHORIZED' && response.order_id) {
          isProcessing.value = false
          router.push({
            name: 'order_status',
            params: { order_id: `${response.order_id}` }
          })
          cartStore.CART_DATA = ''
          return
        }

        if (response.checkout_url) {
          sessionStorage.setItem('mollie_order_id', `${response.order_id}`)
          window.location.href = response.checkout_url
          return
        }

        cardErrorMsg.value = 'Unable to start payment. Please try again.'
        isProcessing.value = false
      } catch (err) {
        cardErrorMsg.value = err?.message || 'Payment failed. Please try again.'
        isProcessing.value = false
      }
    }

    onMounted(async () => {
      isUnmounted.value = false
      try {
        await loadMollieScript()
        mollie.value = window.Mollie(props.profileId, {
          locale: 'en_GB',
          testmode: props.testmode
        })
        mountCardForm()
      } catch (err) {
        cardErrorMsg.value = 'Unable to load Mollie payment form.'
        payButtonDisabled.value = true
      }
    })

    onBeforeUnmount(() => {
      isUnmounted.value = true
      if (cardComponent.value?.unmount) {
        cardComponent.value.unmount()
      }
    })

    watch(
      payButtonDisabled,
      (disabled) => {
        emit('pay-button-state', disabled !== false)
      },
      { immediate: true }
    )

    expose({
      handleMolliePayment,
      handleStripePayment: handleMolliePayment,
      payButtonDisabled
    })

    return {
      cardErrorMsg,
      payButtonDisabled,
      isProcessing,
      formattedAmount,
      handleMolliePayment
    }
  }
}
</script>

<template>
  <div class="mollie-payment-v1">
    <div id="mollie-card-form" class="mollie-card-form"></div>
    <div v-if="cardErrorMsg" class="text-sm font-medium text-red-600 dark:text-red-400 mt-2">
      {{ cardErrorMsg }}
    </div>
    <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
      Pay {{ formattedAmount }} securely by card. Apple Pay may appear during bank verification.
    </p>
  </div>
</template>

<style scoped>
.mollie-card-form {
  border: 1px solid rgba(209, 213, 219, 1);
  border-radius: 0.375rem;
  padding: 12px;
  min-height: 120px;
  background: white;
}

html.dark .mollie-card-form {
  background: #374151;
  border-color: #4b5563;
}
</style>
